import { type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useTransition, config } from '@react-spring/web';
import { useMedia } from 'react-use';
import { CaretDownNew, Tag } from '@hungryroot/icons';
import { Heading, Paragraph } from '@hungryroot/ramen';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  OrderSummary,
  type PromoValue,
} from '../../../../components/OrderSummary/OrderSummary';
import styles from './OrderSummarySection.module.css';

interface Props {
  testid?: string;
  subtotal: number;
  promos?: PromoValue[];
  amountSummary: ReactNode;
  shipping: number;
  orderId?: number;
  credit: number;
  promoText?: string;
  continueButton: ReactNode;
  isCollapsed: boolean;
  setIsCollapsed: Dispatch<SetStateAction<boolean>>;
  discount?: number;
  flexCreditMessaging?: boolean;
  showPricingNudge?: boolean;
  freeShippingDelta?: string | null;
  subtotalLabel?: string;
}

export function OrderSummarySection(props: Props) {
  const {
    testid,
    promos,
    subtotal,
    amountSummary,
    shipping,
    credit,
    orderId,
    promoText,
    continueButton,
    isCollapsed,
    setIsCollapsed,
    discount,
    flexCreditMessaging,
    showPricingNudge,
    freeShippingDelta,
    subtotalLabel,
  } = props;

  const isDesktop = useMedia(BreakpointQueryLarge);

  const collapsedButton = (
    <button
      className={styles.caret}
      onClick={() => setIsCollapsed(!isCollapsed)}
      aria-label={isCollapsed ? 'open' : 'close'}
    >
      <CaretDownNew />
    </button>
  );

  const collapsedState = (
    <div
      className={styles.collapsed}
      data-testid="checkout-order-summary-collapsed"
    >
      {discount !== undefined && discount > 0 && (
        <div className={styles['savings-banner']}>
          <Paragraph className={styles['savings-banner-text']}>
            You're saving
            <Paragraph className={styles['savings-banner-amount']}>
              ${discount.toFixed(2)}
            </Paragraph>
            today
          </Paragraph>
        </div>
      )}
      <div className={styles['collapsed-content']}>
        <div>
          <div className={styles.row}>
            <Heading variant="H5">Order summary</Heading>
            <div className={styles['row-right']}>
              {amountSummary}
              {collapsedButton}
            </div>
          </div>
          {promoText && (
            <Paragraph
              variant="caption"
              className={styles.promo}
              testid="cyo-promo-text"
            >
              <Tag />
              <span className={styles['promo-copy']}>{promoText}</span>
            </Paragraph>
          )}
        </div>
        {continueButton}
      </div>
    </div>
  );

  const expandedTransitions = useTransition(!isCollapsed || isDesktop, {
    config: {
      ...config.stiff,
      duration: 300,
    },
    from: {
      transform: `translate3d(0, 80%, 0)`,
    },
    enter: {
      transform: `translate3d(0, 0%, 0)`,
    },
    leave: {
      transform: `translate3d(0, 80%, 0)`,
    },
  });

  return (
    <div data-testid={testid} className={styles.container}>
      {collapsedState}
      {expandedTransitions((spring, item) => {
        if (item === false) {
          return null;
        }
        return (
          <OrderSummary
            subtotalLabel={subtotalLabel}
            freeShippingDelta={freeShippingDelta}
            showPricingNudge={showPricingNudge}
            testid="checkout-order-summary-open"
            subtotal={subtotal}
            promos={promos}
            amountSummary={amountSummary}
            shipping={shipping}
            orderId={orderId}
            credit={credit}
            setCollapsed={
              !isDesktop ? () => setIsCollapsed(!isCollapsed) : undefined
            }
            containerClassName={styles['open-content']}
            footerContainerClassName={styles['footer-content']}
            spring={spring}
            hidePromo
            totalText="First order total"
            flexCreditMessaging={flexCreditMessaging}
          >
            {!isDesktop && continueButton}
          </OrderSummary>
        );
      })}
    </div>
  );
}
